import { HttpError, useCustom } from "@refinedev/core";
import { config } from "@src/config";
import { MEASURE_RATIO_FIREBASE, MEASURE_RETENTION_BY_EVENT_NAME } from "@src/constant/ab-testing";
import { SupportedModelAlias } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { useABContext } from "@src/contexts/ab-testing/ab-context";
import { FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { IDimension, IMeasure, IMetaResponse, IModelAlias } from "@src/types/ab-testing-exploration";
import { Form } from "antd";
import React from "react";

export const USER_DIMENSIONS: string[] = [
    "device_model",
    "device_category",
    "media_source",
    "country_code",
    "first_app_version",
    "last_app_version",
    "first_signal_date",
    "day0_date",
    "install_date_tzutc",
];
const DAY_DIFF_DIMENSIONS: string[] = ["day_diff", "days_since_install"];
const MEMBER_WHITELIST: Record<
    string,
    Record<"dimensionsTree" | "dimensions" | "measures", string[] | Record<string, string[]>>
> = {
    [SupportedModelAlias.AD_MONETIZATION]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.AD_MONETIZATION]: [...DAY_DIFF_DIMENSIONS, "ad_network", "ad_unit"],
        },
        dimensions: [...DAY_DIFF_DIMENSIONS, ...USER_DIMENSIONS, "ad_network", "ad_unit"],
        measures: ["ad_value", "ad_impressions", "ecpm"],
    },

    [SupportedModelAlias.SUB_MONETIZATION]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.SUB_MONETIZATION]: [...DAY_DIFF_DIMENSIONS, "package_id"],
        },
        dimensions: [...DAY_DIFF_DIMENSIONS, ...USER_DIMENSIONS, "package_id"],
        measures: ["trial_count", "trial_rate", "pay_count", "pay_rate", "total_subscription_value"],
    },

    [SupportedModelAlias.IAP_MONETIZATION]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: [...DAY_DIFF_DIMENSIONS, ...USER_DIMENSIONS],
        },
        dimensions: [...DAY_DIFF_DIMENSIONS, ...USER_DIMENSIONS],
        measures: ["nosub_pay_rate", "iap_pay_count", "total_iap_value"],
    },

    [SupportedModelAlias.FIREBASE]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.FIREBASE]: [
                ...DAY_DIFF_DIMENSIONS,
                "event_name",
                "ga_session_number",
                "event_name",
                "app_version",
            ],
            [SupportedModelAlias.EVENT_PARAM]: ["ep_string_{param_key}", "ep_number_{param_key}"],
        },
        dimensions: [
            ...DAY_DIFF_DIMENSIONS,
            "event_name",
            "ga_session_number",
            "event_name",
            "app_version",
            ...USER_DIMENSIONS,
            "ep_string_{param_key}",
            "ep_number_{param_key}",
        ],
        measures: ["event_count", MEASURE_RATIO_FIREBASE],
    },

    [SupportedModelAlias.FIREBASE_ENGAGEMENT]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.FIREBASE_ENGAGEMENT]: [
                ...DAY_DIFF_DIMENSIONS,
                "event_name",
                "ga_session_number",
                "event_name",
                "app_version",
            ],
        },
        dimensions: [
            ...DAY_DIFF_DIMENSIONS,
            "event_name",
            "ga_session_number",
            "event_name",
            "app_version",
            ...USER_DIMENSIONS,
        ],
        measures: [
            "sum_engagement_time",
            "retention",
            "sum_retention",
            "sum_retention_exclude_day0",
            "retention_by_me_start",
            MEASURE_RETENTION_BY_EVENT_NAME,
        ],
    },

    [SupportedModelAlias.FIREBASE_PLAYTIME]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.FIREBASE_PLAYTIME]: [
                ...DAY_DIFF_DIMENSIONS,
                "event_name",
                "ga_session_number",
                "event_name",
                "app_version",
            ],
        },
        dimensions: [
            ...DAY_DIFF_DIMENSIONS,
            "event_name",
            "ga_session_number",
            "event_name",
            "app_version",
            ...USER_DIMENSIONS,
        ],
        measures: ["sum_song_playtime"],
    },
    [SupportedModelAlias.FIREBASE_ENGAGEMENT_BY_EVENT]: {
        dimensionsTree: {
            [SupportedModelAlias.USER_INFO]: USER_DIMENSIONS,
            [SupportedModelAlias.FIREBASE_ENGAGEMENT_BY_EVENT]: [
                ...DAY_DIFF_DIMENSIONS,
                "event_name",
                "ga_session_number",
                "event_name",
                "app_version",
            ],
        },
        dimensions: [
            ...DAY_DIFF_DIMENSIONS,
            "event_name",
            "ga_session_number",
            "event_name",
            "app_version",
            ...USER_DIMENSIONS,
        ],
        measures: ["retention_by_{event_name}"],
    },
    // [SupportedModelAlias.FIREBASE_ACTIVATION]: {
    //   dimensions: USER_DIMENSIONS,
    //   measures: [
    //     "setup_rate",
    //     "aha_rate",
    //     "habit_rate",
    //   ],
    // },
};

export const useGetModelMember = () => {
    const [excludeDimensions, setExcludeDimensions] = React.useState<string[]>([]);

    const { filterForm } = useABContext();
    const productCode = Form.useWatch(FilterFormFieldsEnum.PRODUCT_CODE, filterForm);
    const {
        data,
        error: loadMetaError,
        isLoading: isLoadingMeta,
        refetch: refreshMeta,
    } = useCustom<IMetaResponse, HttpError>({
        url: `${config.NESTJS_URL}/api/proxy/v2/exploration_v2/ab-test-exploration/meta`,
        method: "get",
        queryOptions: {
            enabled: false,
        },
        dataProviderName: "nestjsx",
        config: {
            headers: {
                "Product-Code": productCode,
            },
        },
    });

    React.useEffect(() => {
        if (!productCode) return;
        refreshMeta();
    }, [JSON.stringify(productCode)]);
    const models = data?.data?.models ?? [];

    // TODO: consider to remove from api response
    const ableToExploreModels: IModelAlias[] = models
        .map(({ alias }) => alias)
        .filter((alias) => alias !== SupportedModelAlias.USER_INFO)
        .filter((alias) => MEMBER_WHITELIST[alias] !== undefined);

    function getAvailableMembers<T>({
        modelAlias,
        memberType,
    }: {
        modelAlias: IModelAlias;
        memberType: "dimensions" | "measures";
    }): T[] | undefined {
        const targetModel = models.find(({ alias }) => {
            return alias === modelAlias;
        });

        if (targetModel) {
            const whitelist = (MEMBER_WHITELIST[modelAlias]?.[memberType] ?? []) as string[];
            return (targetModel[memberType]?.filter(({ name }) => {
                return whitelist.indexOf(name) !== -1 && !excludeDimensions?.includes(name);
            }) || []) as T[];
        }
    }

    function getAvailableDimensionsTree({ modelAlias }: { modelAlias: IModelAlias }): Record<string, IDimension[]> {
        const targetModel = models.find(({ alias }) => {
            return alias === modelAlias;
        });
        const whitelist = (MEMBER_WHITELIST[modelAlias]?.["dimensionsTree"] ?? {}) as Record<string, string[]>;
        return Object.entries(whitelist).reduce((acc, [key, value]) => {
            acc[key] =
                targetModel?.["dimensions"]?.filter(({ name }) => {
                    return value.indexOf(name) !== -1 && !excludeDimensions?.includes(name);
                }) ?? [];
            return acc;
        }, {} as Record<string, IDimension[]>);
    }
    function getAvailableDimensions({ modelAlias }: { modelAlias: IModelAlias }) {
        return getAvailableMembers<IDimension>({ modelAlias, memberType: "dimensions" });
    }

    function getAvailableMeasures({ modelAlias }: { modelAlias: IModelAlias }) {
        return getAvailableMembers<IMeasure>({ modelAlias, memberType: "measures" });
    }
    return {
        isLoadingMeta,
        loadMetaError,
        refreshMeta,
        availableModels: ableToExploreModels,
        getAvailableDimensionsTree,
        getAvailableDimensions,
        getAvailableMeasures,
        setExcludeDimensions,
        excludeDimensions,
    };
};
